import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";

import { usePageDetails } from "../../hooks/usePageDetails";
import CreateHeading from "../../components/CreateHeading/CreateHeading";
import { Layout } from "../../layout/PortalLayout/PortalLayout";
import FormInput from "../../components/FormInput/FormInput";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import FormActionButtons from "../../components/FormActionButtons/FormActionButtons";
import AsyncFormSelect from "../../components/FormSelect/AsyncFormSelect";
import { useLazyGetBrandListQuery } from "../../app/api/brandApi";
import {
  useCreateFamilyMutation,
  useEditFamilyMutation,
  useLazyGetSingleFamilyDetailsQuery,
  useLazyGetTimezoneListQuery,
} from "../../app/api/familyApi";
import { handleApiResponse } from "../../utils/handleApiResponse";
import { useAppSelector } from "../../redux/store";
import { useFilterManager } from "../../hooks/useFilterManager";

export interface InitialFormValues {
  family_name: string;
  is_active: boolean;
  brand_id: string | null;
  timezone_format_id?: string;
}

const CreateFamily: React.FC = () => {
  const { id, pageType } = usePageDetails();
  const navigate = useNavigate();
  const { handleFilterChange } = useFilterManager();
  const persistDropdownSelector = useAppSelector(
    (state) => state.dropdownSelector
  );

  const [getBrandListQuery] = useLazyGetBrandListQuery();
  const [createFamilyMutation] = useCreateFamilyMutation();
  const [getTimezoneListQuery] = useLazyGetTimezoneListQuery();

  const [getSingleFamilyDetailsQuery, getSingleFamilyDetailsResponse] =
    useLazyGetSingleFamilyDetailsQuery();
  const [editFamilyMutation] = useEditFamilyMutation();

  const initialValues: InitialFormValues = {
    family_name: getSingleFamilyDetailsResponse?.data?.data?.family_name ?? "",
    is_active: getSingleFamilyDetailsResponse?.data?.data?.is_active ?? true,
    brand_id: persistDropdownSelector["brand_selector"]?.value ?? "",
    timezone_format_id:
      getSingleFamilyDetailsResponse?.data?.data?.timezone_format_id ?? "",
  };

  const validationSchema = Yup.object().shape({
    family_name: Yup.string()
      .required("Family name is required")
      .min(2, "Family name must be at least 2 character")
      .max(255, "Family name must be at most 255 characters"),
    is_active: Yup.boolean(),
    brand_id: Yup.string().required("Brand is required"),
    timezone_format_id: Yup.string().required("Timezone Format ID is required"),
  });

  const getSingleFamilyDetails = async () => {
    try {
      await getSingleFamilyDetailsQuery(
        {
          family_id: id,
          brand_id: persistDropdownSelector["brand_selector"]?.value,
        },
        true
      ).unwrap();
    } catch (error: any) {
      console.log(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Get Family Details Error",
      });
    }
  };

  useEffect(() => {
    if (pageType !== "create") {
      getSingleFamilyDetails();
    }
    // eslint-disable-next-line
  }, [id]);

  const handleSubmit = async (
    values: InitialFormValues,
    { setFieldError }: FormikHelpers<InitialFormValues>
  ) => {
    const { family_name, brand_id, is_active, timezone_format_id } = values;

    const body = {
      family_name,
      timezone_format_id: Number(timezone_format_id),
      ...(pageType === "edit" ? { is_active } : {}),
    };

    const successMessage =
      pageType === "create"
        ? "Family created successfully."
        : "Family updated successfully.";

    try {
      const response =
        pageType === "create"
          ? await createFamilyMutation({ body, brand_id }).unwrap()
          : await editFamilyMutation({
              family_id: id,
              brand_id,
              body,
            }).unwrap();
      if (response?.status_code === 200 || response?.status_code === 201) {
        handleApiResponse({
          type: "success",
          message: successMessage,
          toastId: "Create Family",
        });
        navigate("/provisioning/family");
      }
    } catch (error: any) {
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Create Family Error",
        setFieldError,
      });

      console.log(error);
    }
  };

  return (
    <Layout>
      <CreateHeading label={"Family"} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
          dirty,
        }) => {
          const disable =
            pageType === "create"
              ? values.family_name.length === 0 ||
                values.brand_id?.length === 0 ||
                values.timezone_format_id?.length === 0
              : !dirty;

          return (
            <>
              <Form className="w-100 mb-5 pb-5">
                <Col sm={6}>
                  {pageType === "view" && (
                    <Row className="my-2">
                      <FormInput
                        label="Family ID"
                        type="text"
                        name="id"
                        placeholder="Enter account name"
                        labelClassName="m-0 fw-500"
                        controlClassName="bg-white fs-15-point-5 h-48"
                        value={
                          getSingleFamilyDetailsResponse?.data?.data?.id ?? ""
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required={false}
                        isValid={touched.family_name && !errors.family_name}
                        isInvalid={touched.family_name && !!errors.family_name}
                        touched={touched.family_name}
                        error={errors.family_name}
                        disabled={pageType === "view"}
                      />
                    </Row>
                  )}

                  {pageType === "create" ? (
                    <AsyncFormSelect
                      label="Brand"
                      name="brand_id"
                      onChange={(val: string | null, label: string) => {
                        handleFilterChange({
                          updatedField: "brand_id",
                          persistedSelection: {
                            label,
                            value: val,
                          },
                        });
                      }}
                      fetchQuery={getBrandListQuery}
                      fetchQueryParams={{
                        startKey: "",
                        page_size: 5,
                      }}
                      optionLabelName="brand_name"
                      placeholder="Select Brand"
                      required
                      shouldFetch
                      persistedOption={
                        persistDropdownSelector["brand_selector"]
                          ? {
                              label:
                                persistDropdownSelector["brand_selector"]
                                  ?.label,
                              value:
                                persistDropdownSelector["brand_selector"]
                                  ?.value,
                            }
                          : null
                      }
                      touched={touched.brand_id}
                      error={errors.brand_id}
                      setFieldTouched={setFieldTouched}
                    />
                  ) : (
                    <Row className="my-2">
                      <FormInput
                        label="Brand Name"
                        type="text"
                        name="brand_name"
                        placeholder="Brand name"
                        labelClassName="m-0 fw-500"
                        controlClassName="bg-white fs-15-point-5 h-48"
                        value={
                          getSingleFamilyDetailsResponse?.data?.data
                            ?.brand_name ?? ""
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required={true}
                        isValid={false}
                        isInvalid={false}
                        touched={false}
                        error={""}
                        disabled
                      />
                    </Row>
                  )}

                  <Row className="my-2">
                    <FormInput
                      label="Family Name"
                      type="text"
                      name="family_name"
                      placeholder="Enter family name"
                      labelClassName="m-0 fw-500"
                      controlClassName="bg-white fs-15-point-5 h-48"
                      value={values.family_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required={true}
                      isValid={touched.family_name && !errors.family_name}
                      isInvalid={touched.family_name && !!errors.family_name}
                      touched={touched.family_name}
                      error={errors.family_name}
                      disabled={pageType === "view"}
                    />
                  </Row>
                  <Row className="my-2">
                    <AsyncFormSelect
                      label="Timezone Format ID"
                      name="timezone_format_id"
                      onChange={(val: string | null) => {
                        setFieldValue("timezone_format_id", val);
                      }}
                      fetchQuery={getTimezoneListQuery}
                      optionLabelName="value"
                      placeholder="Select Timezone Format ID"
                      required
                      shouldFetch
                      touched={touched.timezone_format_id}
                      error={errors.timezone_format_id}
                      setFieldTouched={setFieldTouched}
                      value={values.timezone_format_id}
                      isDisabled={pageType === "view"}
                    />
                  </Row>

                  {pageType !== "create" && (
                    <Row className="ms-0 mt-2">
                      <ToggleSwitch
                        activeStatusText="Active"
                        inActiveStatusText="Inactive"
                        value={values.is_active}
                        onChange={(val: boolean) => {
                          setFieldValue("is_active", val);
                        }}
                        key={String(values.is_active)}
                        isDisabled={pageType === "view"}
                      />
                    </Row>
                  )}

                  <FormActionButtons
                    disable={disable}
                    isSubmitting={isSubmitting}
                    navigateUrl="/provisioning/family"
                  />
                </Col>
              </Form>
            </>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default CreateFamily;
